import { adapter } from '@seek/tealium-adapter';

import type {
  HeaderFooterEventName,
  HeaderFooterAnalytics,
  HeaderFooterLinkClickedProps,
  HeaderFooterNavigationPressedProps,
} from './headerFooter';

import {
  configureAdapter,
  type DataLayer,
  type Experiments,
  type SetExperiments,
  type TrackFn,
  trackFunction,
} from '.';

export type EventName =
  | 'saved_searches_displayed'
  | 'jobmail_unsubscribe_succeeded'
  | 'jobmail_subscribe_succeeded'
  | 'saved_search_delete_succeeded'
  | 'saved_search_pressed'
  | 'account_settings_pressed'
  | HeaderFooterEventName;

export interface SavedSearchesAnalytics
  extends HeaderFooterAnalytics,
    SetExperiments {
  savedSearchesPageLoaded: (dataLayer: DataLayer) => void;
  jobMailUnsubscribeSucceeded: () => void;
  jobMailSubscribeSucceeded: () => void;
  savedSearchDeleteSucceeded: () => void;
  savedSearchPressed: () => void;
  manageEmailPressed: () => void;
}

const defaultInitialDataLayerSavedSearches = {
  currentPage: 'saved searches',
  experimentId: undefined,
};

export default (initialValues?: any): SavedSearchesAnalytics => {
  const tealium = adapter({});
  configureAdapter(tealium);

  let dataLayer: DataLayer = {
    ...defaultInitialDataLayerSavedSearches,
    ...initialValues,
  };

  const track: TrackFn<EventName, DataLayer> = trackFunction;

  const savedSearchesPageLoaded = ({
    profileGuid,
    loginId,
    brand,
    siteCountry,
  }: DataLayer) => {
    dataLayer = {
      ...dataLayer,
      profileGuid,
      loginId,
      brand,
      siteCountry,
    };

    track('view', 'saved_searches_displayed', dataLayer);
  };

  const jobMailUnsubscribeSucceeded = () => {
    track('link', 'jobmail_unsubscribe_succeeded', {
      ...dataLayer,
      linkName: 'jobmail_unsubscribe_succeeded',
      isThirdPartyLink: false,
    });
  };

  const jobMailSubscribeSucceeded = () => {
    track('link', 'jobmail_subscribe_succeeded', {
      ...dataLayer,
      linkName: 'jobmail_subscribe_succeeded',
      isThirdPartyLink: false,
    });
  };

  const savedSearchDeleteSucceeded = () => {
    track('link', 'saved_search_delete_succeeded', {
      ...dataLayer,
      linkName: 'saved_search_delete_succeeded',
      isThirdPartyLink: false,
    });
  };

  const savedSearchPressed = () => {
    track('link', 'saved_search_pressed', {
      ...dataLayer,
      linkName: 'saved_search_pressed',
      isThirdPartyLink: false,
    });
  };

  const manageEmailPressed = () => {
    track('link', 'account_settings_pressed', {
      ...dataLayer,
      linkName: 'account_settings_pressed',
      linkPosition: 'body',
      isThirdPartyLink: false,
    });
  };

  const headerFooterLinkClicked = (props: HeaderFooterLinkClickedProps) => {
    track('link', 'navigation_pressed', {
      ...dataLayer,
      ...props,
    });
  };

  const headerFooterNavigationPressed = (
    props: HeaderFooterNavigationPressedProps,
  ) => {
    track('link', 'navigation_pressed', {
      ...dataLayer,
      ...props,
    });
  };

  const setExperiments = ({
    experimentsEligible,
    hubbleExperiments,
  }: Experiments) => {
    dataLayer.experimentsEligible = experimentsEligible;
    dataLayer.hubbleExperiments = hubbleExperiments;
  };

  return {
    headerFooterLinkClicked,
    headerFooterNavigationPressed,
    savedSearchesPageLoaded,
    jobMailUnsubscribeSucceeded,
    jobMailSubscribeSucceeded,
    savedSearchDeleteSucceeded,
    savedSearchPressed,
    manageEmailPressed,
    setExperiments,
  };
};
